import * as React from "react";
import { useParams, useOutletContext } from "react-router-dom";
import toast from "react-hot-toast";
import axiosInstant from "../../api";
import BrandForm, { SaveChangesParams } from "./BrandForm";
import { createProductForBrand, rmnPlanEndpoint } from "../../api/endpoints";
import { IProduct } from "./components/ProductList";
import { IBrandDetailsContext } from "./SingleBrandDetails";

export default function BrandDetail() {
  const { id } = useParams();

  const {
    brand,
    setBrand,
    brandProducts: prevProducts,
    brandExperts: prevPros,
  } = useOutletContext() as IBrandDetailsContext;

  // Products with PDP scoring active (what the plan's max_products caps),
  // from the workspace plan meter — not the catalog size.
  const [scoringCount, setScoringCount] = React.useState<number | undefined>(undefined);
  React.useEffect(() => {
    if (!id) return;
    axiosInstant
      .get(rmnPlanEndpoint, { params: { rmn_brand_id: id } })
      .then((res) => setScoringCount(res.data?.product_count))
      .catch(() => setScoringCount(undefined));
  }, [id, brand?.plan, brand?.max_products]);

  const createProduct = async (brandId: string, product: IProduct) => {
    const payload: any = {
      name: product.title,
      brandId,
      description: product.description,
      image: product.image || undefined,
    };

    const endpoint = createProductForBrand(brandId.toString());
    try {
      const { data } = await axiosInstant.post(endpoint, payload);
      if (data?.desireid) return data.desireid as string;
    } catch (error) {
      toast.error((error as Error).message);
    }
    return null;
  };

  const handleSaveChanges: SaveChangesParams = async (
    payload: any,
    productIds,
    newProducts,
    prosIds,
    contentTypeIds,
  ) => {
    try {
      if (payload.logo_image) payload.logo = payload.logo_image;
      else payload.logo = brand.logo;

      const { data } = await axiosInstant.patch(`/brands/${id}`, payload);

      if (data?.id) {
        const brandId = data.id;
        // Plan / rmn / limits come back derived from the API's single plan
        // writer — sync them so the menu chip and Network tab react.
        setBrand((prev: any) => ({
          ...prev,
          plan: data.plan,
          rmn: data.rmn,
          max_products: data.max_products,
          max_pdp_score_interval: data.max_pdp_score_interval,
        }));
        await axiosInstant.post(`/brands/${brandId}/pros`, {
          user_ids: prosIds,
        });

        const newProductPromises = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const newProduct of newProducts) {
          if (newProduct.title)
            newProductPromises.push(createProduct(brandId, newProduct));
        }
        const newProductIds = (await Promise.all(newProductPromises)).filter(
          (e) => !!e,
        );

        const allProductIds = [...productIds, ...newProductIds];

        const currentProductsResponse = await axiosInstant.get(
          `/brands/${brandId}/desires`,
        );
        const currentProducts = currentProductsResponse?.data?.rows || [];
        const currentProductIds = currentProducts.map((p: any) =>
          p.desireid.toString(),
        );

        const productsToRemove = currentProductIds.filter(
          (_id: any) =>
            !allProductIds.map((pid: any) => pid.toString()).includes(_id),
        );

        const productsToAdd = allProductIds.filter(
          (_id: any) => !currentProductIds.includes(_id.toString()),
        );

        const productDeletionPromises: Promise<any>[] = [];
        productsToRemove.forEach((productId: any) => {
          productDeletionPromises.push(
            axiosInstant.delete(`/brands/${brandId}/products/${productId}`),
          );
        });
        const deletedProductsResults = await Promise.allSettled(
          productDeletionPromises,
        );
        if (deletedProductsResults.some((r) => r.status === "rejected")) {
          toast.error("Failed to remove some products.");
        }

        if (productsToAdd.length > 0) {
          await axiosInstant.post(`/brands/${brandId}/products`, {
            desire_ids: productsToAdd,
          });
        }

        if (contentTypeIds && contentTypeIds.length >= 0) {
          try {
            const currentContentTypesResponse = await axiosInstant.get(
              `/content-types/brand/${brandId}`,
            );
            const currentContentTypes = currentContentTypesResponse?.data || [];
            const currentContentTypeIds = currentContentTypes.map(
              (ct: any) => ct.id,
            );

            const contentTypesToRemove = currentContentTypeIds.filter(
              (_id: number) => !contentTypeIds.includes(_id),
            );

            const contentTypesToAdd = contentTypeIds.filter(
              (_id: number) => !currentContentTypeIds.includes(_id),
            );

            const typeDeletionPromises: Promise<any>[] = [];
            contentTypesToRemove.forEach((_id: number) => {
              typeDeletionPromises.push(
                axiosInstant.delete(`/brands/${brandId}/content-types/${_id}`),
              );
            });
            const deletedTypesResults = await Promise.allSettled(
              typeDeletionPromises,
            );
            if (deletedTypesResults.some((r) => r.status === "rejected")) {
              toast.error("Failed to remove some content types.");
            }

            if (contentTypesToAdd.length > 0) {
              await axiosInstant.post(`/brands/${brandId}/content-types`, {
                content_type_ids: contentTypesToAdd,
              });
            }
          } catch (error) {
            console.error("Failed to update content type associations:", error);
          }
        }

        toast.success("Brand updated successfully!");
      }
    } catch (error) {
      toast.error("Failed to update brand. Please try again.");
    }
  };

  return (
    <BrandForm
      brandData={brand}
      prevProducts={prevProducts}
      prevPros={prevPros}
      productCount={scoringCount}
      saveChanges={handleSaveChanges}
      onNameChange={(name) => setBrand((prev) => ({ ...prev, name }))}
    />
  );
}
